import { useState, useCallback, useEffect } from 'react';
import { useSchemaDiscoveryWebSocket } from './useSchemaDiscoveryWebSocket';
import { useAnalyticsWebSocket } from './useAnalyticsWebSocket';
import { getServiceRegistry } from '@/services/integration/ServiceRegistry';
import { useApiClient } from '@/hooks/use-api-client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/utils';

export type SetupStep =
  | 'connection'
  | 'connecting'
  | 'testing'
  | 'discovery'
  | 'schema-generation'
  | 'deployment'
  | 'complete'
  | 'error';

export interface SetupConfig {
  dataSource: {
    name: string;
    type: string;
    config: Record<string, any>;
  };
  options: {
    testConnection?: boolean;
    aiEnhancement?: boolean;
    industryContext?: string;
    generateCubeSchema?: boolean;
    deployImmediately?: boolean;
    optimizationLevel?: 'performance' | 'accuracy' | 'balanced';
  };
}

export interface SetupState {
  currentStep: SetupStep;
  progress: number;
  dataSource: any | null;
  discoveredSchema: any | null;
  cubeSchema: any | null;
  error: string | null;
  isComplete: boolean;
  canRetry: boolean;
  stepHistory: Array<{
    step: SetupStep;
    timestamp: Date;
    success: boolean;
    message?: string;
  }>;
}

export const useDataSourceSetup = () => {
  const [state, setState] = useState<SetupState>({
    currentStep: 'connection',
    progress: 0,
    dataSource: null,
    discoveredSchema: null,
    cubeSchema: null,
    error: null,
    isComplete: false,
    canRetry: false,
    stepHistory: []
  });

  const apiClient = useApiClient();
  const serviceRegistry = getServiceRegistry(apiClient);
  const { toast } = useToast();

  const schemaWebSocket = useSchemaDiscoveryWebSocket({
    autoConnect: false // We'll connect when needed
  });

  const analyticsWebSocket = useAnalyticsWebSocket({
    autoConnect: true
  });

  const addStepToHistory = useCallback((step: SetupStep, success: boolean, message?: string) => {
    setState(prev => ({
      ...prev,
      stepHistory: [
        ...prev.stepHistory,
        {
          step,
          timestamp: new Date(),
          success,
          message
        }
      ]
    }));
  }, []);

  const updateProgress = useCallback((step: SetupStep, progress: number, error?: string) => {
    setState(prev => ({
      ...prev,
      currentStep: step,
      progress,
      error: error || null,
      canRetry: !!error
    }));
  }, []);

  // Set up WebSocket event handlers
  useEffect(() => {
    // Schema discovery progress
    schemaWebSocket.on('schema-discovery-progress', (data) => {
      updateProgress('discovery', data.progress);
    });

    schemaWebSocket.on('schema-discovery-completed', (data) => {
      setState(prev => ({
        ...prev,
        discoveredSchema: data.schema
      }));
      addStepToHistory('discovery', true, 'Schema discovery completed successfully');

      if (state.dataSource) {
        generateCubeSchema();
      }
    });

    schemaWebSocket.on('cube-schema-generated', (data) => {
      setState(prev => ({
        ...prev,
        cubeSchema: data.cubeSchema
      }));
      addStepToHistory('schema-generation', true, 'Cube schema generated successfully');
      updateProgress('deployment', 90);

      // Auto-deploy if configured
      if (currentConfig?.options.deployImmediately) {
        deployCubeSchema(data.cubeSchema);
      } else {
        completeSetup();
      }
    });

    schemaWebSocket.on('schema-discovery-error', (data) => {
      addStepToHistory(state.currentStep, false, data.error);
      updateProgress('error', state.progress, data.error);
    });

    // Analytics events
    analyticsWebSocket.on('data-source-connected', (data) => {
      if (data.dataSourceId === state.dataSource?.id) {
        addStepToHistory('connecting', true, 'Data source connected successfully');
        startSchemaDiscovery();
      }
    });

    analyticsWebSocket.on('data-source-error', (data) => {
      if (data.dataSourceId === state.dataSource?.id) {
        addStepToHistory('connecting', false, data.error);
        updateProgress('error', state.progress, data.error);
      }
    });

    analyticsWebSocket.on('cube-deployed', (data) => {
      if (data.dataSourceId === state.dataSource?.id) {
        addStepToHistory('deployment', true, 'Cube schema deployed successfully');
        completeSetup();
      }
    });

    return () => {
      schemaWebSocket.off('schema-discovery-progress');
      schemaWebSocket.off('schema-discovery-completed');
      schemaWebSocket.off('cube-schema-generated');
      schemaWebSocket.off('schema-discovery-error');
      analyticsWebSocket.off('data-source-connected');
      analyticsWebSocket.off('data-source-error');
      analyticsWebSocket.off('cube-deployed');
    };
  }, [state.currentStep, state.progress, state.dataSource]);

  let currentConfig: SetupConfig | null = null;

  const executeSetupWorkflow = useCallback(async (config: SetupConfig) => {
    currentConfig = config;
    logger.info('Starting data source setup workflow', config);

    try {
      // Reset state
      setState({
        currentStep: 'connection',
        progress: 0,
        dataSource: null,
        discoveredSchema: null,
        cubeSchema: null,
        error: null,
        isComplete: false,
        canRetry: false,
        stepHistory: []
      });

      // Step 1: Create and test connection
      updateProgress('connecting', 10);
      const dataSource = await createDataSource(config.dataSource);

      setState(prev => ({ ...prev, dataSource }));
      addStepToHistory('connection', true, 'Data source created successfully');

      if (config.options.testConnection !== false) {
        updateProgress('testing', 25);
        await testConnection(dataSource);
        addStepToHistory('testing', true, 'Connection test passed');
      }

      // Connect to WebSocket for real-time updates
      if (!schemaWebSocket.isConnected) {
        await schemaWebSocket.connect();
      }

      updateProgress('discovery', 40);
      // Schema discovery will be triggered by the WebSocket event

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Setup failed';
      addStepToHistory(state.currentStep, false, errorMessage);
      updateProgress('error', state.progress, errorMessage);

      toast({
        title: "Setup Failed",
        description: errorMessage,
        variant: "destructive"
      });
    }
  }, [schemaWebSocket, state.currentStep, state.progress, toast]);

  const createDataSource = async (config: SetupConfig['dataSource']) => {
    try {
      const response = await serviceRegistry.executeOperation('create-data-source', {
        name: config.name,
        type: config.type,
        configuration: config.config
      });

      return response.dataSource;
    } catch (error) {
      logger.error('Failed to create data source', error);
      throw new Error(`Failed to create data source: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const testConnection = async (dataSource: any) => {
    try {
      const response = await serviceRegistry.executeOperation('test-data-source-connection', {
        dataSourceId: dataSource.id
      });

      if (!response.success) {
        throw new Error(response.error || 'Connection test failed');
      }

      return response;
    } catch (error) {
      logger.error('Connection test failed', error);
      throw error;
    }
  };

  const startSchemaDiscovery = useCallback(() => {
    if (!state.dataSource || !currentConfig) {
      return;
    }

    updateProgress('discovery', 50);

    schemaWebSocket.startSchemaDiscovery(state.dataSource.id, {
      industryContext: currentConfig.options.industryContext,
      aiEnhancement: currentConfig.options.aiEnhancement,
      validationLevel: 'comprehensive'
    });
  }, [state.dataSource, schemaWebSocket]);

  const generateCubeSchema = useCallback(() => {
    if (!state.discoveredSchema || !currentConfig?.options.generateCubeSchema) {
      completeSetup();
      return;
    }

    updateProgress('schema-generation', 75);

    schemaWebSocket.generateCubeSchema(state.discoveredSchema.id, {
      optimizationLevel: currentConfig.options.optimizationLevel,
      includePreAggregations: currentConfig.options.optimizationLevel === 'performance'
    });
  }, [state.discoveredSchema, schemaWebSocket]);

  const deployCubeSchema = async (cubeSchema: any) => {
    try {
      updateProgress('deployment', 95);

      await serviceRegistry.executeOperation('deploy-cube-schema', {
        dataSourceId: state.dataSource?.id,
        schemaId: cubeSchema.id,
        schema: cubeSchema
      });

      addStepToHistory('deployment', true, 'Cube schema deployed successfully');
      completeSetup();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Deployment failed';
      addStepToHistory('deployment', false, errorMessage);
      updateProgress('error', state.progress, errorMessage);
    }
  };

  const completeSetup = useCallback(() => {
    updateProgress('complete', 100);
    setState(prev => ({ ...prev, isComplete: true }));

    toast({
      title: "Setup Complete!",
      description: "Data source has been successfully configured and is ready for analytics",
    });

    logger.info('Data source setup workflow completed successfully');
  }, [toast]);

  const retryFromCurrentStep = useCallback(() => {
    if (!state.canRetry || !currentConfig) {
      return;
    }

    logger.info('Retrying setup workflow', { currentStep: state.currentStep });

    switch (state.currentStep) {
      case 'error':
        // Retry the last failed step
        const lastFailedStep = state.stepHistory
          .reverse()
          .find(step => !step.success);

        if (lastFailedStep) {
          setState(prev => ({
            ...prev,
            currentStep: lastFailedStep.step,
            error: null,
            canRetry: false
          }));

          // Re-execute based on the failed step
          if (lastFailedStep.step === 'connecting' || lastFailedStep.step === 'testing') {
            executeSetupWorkflow(currentConfig);
          } else if (lastFailedStep.step === 'discovery') {
            startSchemaDiscovery();
          } else if (lastFailedStep.step === 'schema-generation') {
            generateCubeSchema();
          }
        }
        break;

      default:
        // Restart the entire workflow
        executeSetupWorkflow(currentConfig);
        break;
    }
  }, [state.canRetry, state.currentStep, state.stepHistory, executeSetupWorkflow, startSchemaDiscovery, generateCubeSchema]);

  const cancelSetup = useCallback(() => {
    if (schemaWebSocket.isDiscovering) {
      schemaWebSocket.cancelDiscovery();
    }

    setState(prev => ({
      ...prev,
      currentStep: 'connection',
      progress: 0,
      error: 'Setup cancelled by user',
      canRetry: true
    }));

    addStepToHistory(state.currentStep, false, 'Setup cancelled by user');
  }, [schemaWebSocket, state.currentStep]);

  const resetSetup = useCallback(() => {
    setState({
      currentStep: 'connection',
      progress: 0,
      dataSource: null,
      discoveredSchema: null,
      cubeSchema: null,
      error: null,
      isComplete: false,
      canRetry: false,
      stepHistory: []
    });

    schemaWebSocket.resetState();
    currentConfig = null;
  }, [schemaWebSocket]);

  return {
    // State
    ...state,

    // WebSocket states
    schemaDiscoveryState: schemaWebSocket,
    analyticsState: analyticsWebSocket,

    // Actions
    executeSetupWorkflow,
    retryFromCurrentStep,
    cancelSetup,
    resetSetup,

    // Manual actions
    testDataSourceConnection: (dataSourceId: string) => analyticsWebSocket.testDataSourceConnection(dataSourceId),
    refreshSchema: (dataSourceId: string) => analyticsWebSocket.refreshDataSourceSchema(dataSourceId),

    // Utilities
    isSetupInProgress: state.currentStep !== 'connection' && state.currentStep !== 'complete' && state.currentStep !== 'error',
    hasDataSource: !!state.dataSource,
    hasDiscoveredSchema: !!state.discoveredSchema,
    hasCubeSchema: !!state.cubeSchema,
    progressPercentage: Math.round(state.progress),

    // Step information
    getStepMessage: () => {
      switch (state.currentStep) {
        case 'connection': return 'Ready to begin setup';
        case 'connecting': return 'Establishing connection to data source...';
        case 'testing': return 'Testing connection and validating access...';
        case 'discovery': return 'Analyzing data structure with AI...';
        case 'schema-generation': return 'Generating optimized Cube.js schema...';
        case 'deployment': return 'Deploying schema to analytics engine...';
        case 'complete': return 'Setup completed successfully!';
        case 'error': return state.error || 'An error occurred during setup';
        default: return '';
      }
    },

    getStepIcon: () => {
      // This would return appropriate icon components based on current step
      return state.currentStep;
    }
  };
};